import { balanceStats, flowStats, fmt, heat, priorities, sgn, weakestLeaves } from '@balansa/shared';
import { parentPath } from '../lib/helpers';
import { useApp, useWheel } from '../store';
import { Cn } from './Cn';

export function AnalysisTab() {
  const { wheel, idx, en } = useWheel();
  const A = useApp.getState;
  const th = wheel.settings.threshold;
  const tops = idx.tops;
  if (!tops.length) return <p className="empty">Nav datu analīzei.</p>;
  const bs = balanceStats(idx, th);
  const fs = flowStats(idx, wheel.links);
  const pr = priorities(idx, wheel.links, en);
  const maxS = Math.max(...pr.map((r) => r.score), 0.0001);
  const unfed = bs.low.filter((t) => fs.get(t)!.inGive === 0);
  const takes = wheel.links.filter((l) => l.type === 'take' && idx.node.has(l.fromId) && idx.node.has(l.toId) && idx.top.get(l.fromId) !== idx.top.get(l.toId));
  const leaves = weakestLeaves(idx);
  const focusRow = (id: string) => ({
    onClick: () => A().pick(id),
    onMouseEnter: () => A().setHover(id, null)
  });
  const z = (n: number) => (n ? <td>{n}</td> : <td className="z">·</td>);

  return (
    <div id="analysis" onMouseLeave={() => A().setHover(null, null)}>
      <div className="stats">
        <div className="stat">
          <span>Vidējais līmenis</span>
          <b>
            {fmt(bs.avg)}
            <em> /10</em>
          </b>
          <small>galvenās jomas</small>
        </div>
        <div className="stat">
          <span>Robainums (σ)</span>
          <b>{fmt(bs.sd)}</b>
          <small>{bs.verdict.short}</small>
        </div>
        <div className="stat">
          <span>Trūkums (≤ {th})</span>
          <b>
            {bs.low.length}
            <em> /{tops.length}</em>
          </b>
          <small>{bs.low.length ? bs.low.map((t) => idx.node.get(t)!.name).join(', ') : 'nav'}</small>
        </div>
        <div className="stat">
          <span>Enerģija tagad</span>
          <b>
            {Math.round(en.L)}
            <em> /{Math.round(en.M.cap)}</em>
          </b>
          <small>bilance {sgn(en.M.net)} v. diennaktī</small>
        </div>
      </div>
      <p className="verdict">
        {bs.verdict.long} Vājākā joma: <Cn idx={idx} id={bs.min!} /> ({fmt(idx.val.get(bs.min!)!)}), spēcīgākā: <Cn idx={idx} id={bs.max!} /> (
        {fmt(idx.val.get(bs.max!)!)}).
      </p>

      <h3 className="h3">Kur ieguldīt vispirms</h3>
      <p className="muted">
        Prioritāte = trūkums (10 − vērtība) × ietekme. Ietekme aug, ja joma baro citas jomas, ja citas no tās atņem un ja tās uzlabošana dotu vairāk enerģijas
        baterijai.
      </p>
      {pr.length ? (
        <ol className="prio">
          {pr.map((r) => {
            const why: React.ReactNode[] = [
              r.value <= th ? (
                <span key="v" className="bad">
                  trūkums — {fmt(r.value)}
                </span>
              ) : (
                'vērtība ' + fmt(r.value)
              )
            ];
            if (r.feeds.length) why.push('baro: ' + r.feeds.map((x) => idx.node.get(x)!.name).join(', '));
            if (r.drainedBy.length) why.push('no tās atņem: ' + r.drainedBy.map((x) => idx.node.get(x)!.name).join(', '));
            if (r.energyUpside >= 1) why.push('enerģija ' + sgn(r.energyUpside) + ' v. diennaktī, ja uzlabotu');
            if (!r.feeds.length && !r.drainedBy.length && r.energyUpside < 1) why.push('saišu ar citām jomām nav');
            return (
              <li key={r.id} {...focusRow(r.id)}>
                <div className="prio-h">
                  <Cn idx={idx} id={r.id} />
                  <span className="bar">
                    <i style={{ width: ((r.score / maxS) * 100).toFixed(1) + '%' }} />
                  </span>
                  <b>{fmt(r.score)}</b>
                </div>
                <div className="prio-r">
                  {why.map((w, i) => (
                    <span key={i}>
                      {i ? ' · ' : ''}
                      {w}
                    </span>
                  ))}
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="empty">Visas jomas ir 10 — nav ko uzlabot.</p>
      )}

      {unfed.length ? (
        <>
          <h3 className="h3">Zemas jomas bez atbalsta</h3>
          <p className="muted">Nevienā saitē tās nesaņem resursus no citām jomām. Kura joma tās varētu barot?</p>
          <div className="tags">
            {unfed.map((t) => (
              <Cn key={t} idx={idx} id={t} />
            ))}
          </div>
        </>
      ) : null}

      {takes.length ? (
        <>
          <h3 className="h3">Kas atņem resursus</h3>
          <ul className="risk">
            {takes
              .slice()
              .sort((a, b) => idx.val.get(a.toId)! - idx.val.get(b.toId)!)
              .map((l) => {
                const tv = idx.val.get(l.toId)!;
                return (
                  <li key={l.id} onMouseEnter={() => A().setHover(null, l.id)}>
                    <Cn idx={idx} id={l.fromId} /> <span className="bad">atņem{l.label ? ' ' + l.label : ''}</span> → <Cn idx={idx} id={l.toId} />
                    <b className={tv <= th ? 'bad' : ''}>{fmt(tv)}</b>
                  </li>
                );
              })}
          </ul>
        </>
      ) : null}

      {leaves.length ? (
        <>
          <h3 className="h3">Vājākās apakšjomas</h3>
          <ul className="weak">
            {leaves.map((id) => {
              const v = idx.val.get(id)!;
              return (
                <li key={id} {...focusRow(id)}>
                  <span>
                    <Cn idx={idx} id={id} /> <span className="path">{parentPath(idx, id)}</span>
                  </span>
                  <b style={{ color: heat(v) }}>{fmt(v)}</b>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      <h3 className="h3">Plūsmu bilance</h3>
      <table className="flows">
        <thead>
          <tr>
            <th>Joma</th>
            <th title="Ko dod citām jomām">dod</th>
            <th title="Ko saņem no citām">saņem</th>
            <th title="Ko atņem citām">atņem</th>
            <th title="Ko citas atņem tai">zaudē</th>
          </tr>
        </thead>
        <tbody>
          {tops.map((t) => {
            const f = fs.get(t)!;
            return (
              <tr key={t} {...focusRow(t)}>
                <td>
                  <Cn idx={idx} id={t} />
                </td>
                {z(f.outGive)}
                {z(f.inGive)}
                {z(f.outTake)}
                {z(f.inTake)}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted" style={{ marginTop: 6 }}>
        Skaitļi — saišu stiprumu summa (vāja 1, vidēja 2, spēcīga 3).
      </p>
    </div>
  );
}
